import { createSlice } from '@reduxjs/toolkit';
import { authAPI } from '../../utils/api';
import { clearCachedData } from '../../utils/cachedData';

const initialState = {
  user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null,
  token: localStorage.getItem('token'),
  isAuthenticated: Boolean(localStorage.getItem('token')),
  loading: false,
  error: null
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
      // Drop any cached gym/permissions from a previous session (same tab)
      clearCachedData();
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Clear cached gym/permissions so the next user doesn't see stale data
      clearCachedData();
    },
    updateUserProfile: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    }
  }
});

export const { loginStart, loginSuccess, loginFailure, logout, updateUserProfile, clearError } = authSlice.actions;

// Async thunk for login
export const login = (credentials) => async (dispatch) => {
  try {
    dispatch(loginStart());
    
    // Call the login API
    const response = await authAPI.login(credentials);
    
    // allowedMenus is now returned directly from the login endpoint (role-aware)
    dispatch(loginSuccess(response.data));
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
    dispatch(loginFailure(errorMessage));
    throw error;
  }
};

// Async thunk for registration
export const register = (userData) => async (dispatch) => {
  try {
    dispatch(loginStart());
    
    // Call the register API
    const response = await authAPI.register(userData);
    dispatch(loginSuccess(response.data));
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
    dispatch(loginFailure(errorMessage));
    throw error;
  }
};

// Async thunk to verify token and load user data
export const loadUser = () => async (dispatch) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }
    
    dispatch(loginStart());
    const response = await authAPI.verifyToken();
    dispatch(loginSuccess({ user: response.data.user, token }));
  } catch (error) {
    dispatch(logout());
  }
};

export default authSlice.reducer;
